import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpEventType } from '@angular/common/http';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { FolderService } from '../../core/services/folder.service';
import { DocumentService } from '../../core/services/document.service';
import { NotificationService } from '../../core/services/notification.service';
import { TokenService } from '../../core/services/token.service';
import { I18nService } from '../../core/services/i18n.service';
import { FolderDto, FolderTreeNode } from '../../core/models/folder.model';
import { DocumentDto } from '../../core/models/document.model';
import { FolderTreeComponent } from '../folders/folder-tree.component';
import { DocumentListComponent } from './document-list.component';
import {
  canPreviewMimeType,
  createObjectUrl,
  downloadBlob,
  getFileNameFromContentDisposition,
  revokeObjectUrl
} from '../../shared/utils/file.util';
import { getDocumentActionErrorMessage } from '../../shared/utils/api-error.util';

@Component({
  selector: 'app-archive-page',
  standalone: true,
  imports: [CommonModule, FormsModule, FolderTreeComponent, DocumentListComponent],
  template: `
    <div class="page-title">
      <h2>{{ i18n.t('archive.title') }}</h2>
      <p>{{ i18n.t('archive.subtitle') }}</p>
    </div>

    <div class="actions-card">
      <div class="row">
        <input [(ngModel)]="newFolderName" [placeholder]="i18n.t('archive.newFolderPlaceholder')" />
        <button (click)="createFolder()" [disabled]="!newFolderName.trim()">{{ i18n.t('actions.createFolder') }}</button>
      </div>

      <div class="row upload-row">
        <input [(ngModel)]="uploadTitle" [placeholder]="i18n.t('archive.documentTitlePlaceholder')" />
        <input [(ngModel)]="uploadDescription" [placeholder]="i18n.t('archive.descriptionPlaceholder')" />
        <input type="file" (change)="onFileSelected($event)" />
        <button (click)="uploadDocument()" [disabled]="!canUpload() || isUploading">
          {{ isUploading ? i18n.t('actions.uploading') : i18n.t('actions.upload') }}
        </button>
      </div>

      <div class="upload-progress" *ngIf="isUploading || uploadProgress > 0">
        <div class="upload-progress-header">
          <span>{{ uploadFileName || i18n.t('archive.uploadingDocument') }}</span>
          <span>{{ uploadProgress }}%</span>
        </div>
        <div class="upload-progress-track" aria-hidden="true">
          <div class="upload-progress-fill" [style.width.%]="uploadProgress"></div>
        </div>
      </div>
    </div>

    <div class="grid">
      <div class="left-panel">
        <div class="panel-title">{{ i18n.t('archive.folders') }}</div>
        <app-folder-tree
          [nodes]="folderTree"
          [selectedFolderId]="selectedFolder?.id ?? null"
          (rootSelected)="selectRoot()"
          (folderSelected)="onFolderSelected($event)">
        </app-folder-tree>
        <br/>
        <button
          class="danger"
          (click)="deleteSelectedFolder()"
          [disabled]="!selectedFolder">
          {{ i18n.t('actions.deleteFolder') }}
        </button>
      </div>

      <div class="right-panel">
        <div class="panel-title-row">
          <div class="panel-title">
            {{ documentPanelTitle }}
          </div>
          <div class="search-row">
            <input
              [(ngModel)]="searchQuery"
              (keydown.enter)="applySearch()"
              [placeholder]="i18n.t('archive.searchPlaceholder')" />
            <button type="button" (click)="applySearch()">{{ i18n.t('actions.search') }}</button>
            <button type="button" class="secondary-btn" (click)="clearSearch()" [disabled]="!searchQuery.trim() && !isSearchActive">
              {{ i18n.t('actions.clear') }}
            </button>
          </div>
        </div>

        <div class="search-summary" *ngIf="isSearchActive">
          {{ searchSummaryText }}
        </div>

        <app-document-list
          [documents]="documents"
          [canDelete]="canDeleteDocuments"
          (open)="openDocument($event)"
          (delete)="deleteDocument($event)">
        </app-document-list>
      </div>
    </div>

    <div class="preview-backdrop" *ngIf="previewUrl && previewDocument" (click)="closePreview()">
      <div class="preview-modal" (click)="$event.stopPropagation()">
        <div class="preview-header">
          <div>
            <h3>{{ previewDocument.title }}</h3>
            <p>{{ previewDocument.mimeType }}</p>
          </div>
          <button type="button" class="preview-close" (click)="closePreview()">{{ i18n.t('actions.close') }}</button>
        </div>

        <div class="preview-body">
          <iframe
            *ngIf="previewDocument.mimeType === 'application/pdf'"
            [src]="previewResourceUrl"
            title="PDF preview">
          </iframe>

          <img
            *ngIf="previewDocument.mimeType.startsWith('image/')"
            [src]="previewUrl"
            [alt]="previewDocument.title" />
        </div>
      </div>
    </div>
  `,
  styles: [`
    .page-title { margin-bottom:20px; }
    .actions-card { background:#fff; border:1px solid #e5e7eb; border-radius:16px; padding:16px; margin-bottom:20px; }
    .row { display:flex; gap:12px; margin-bottom:12px; flex-wrap:wrap; }
    .row:last-child { margin-bottom:0; }
    input { height:42px; border:1px solid #d1d5db; border-radius:10px; padding:0 12px; min-width:220px; }
    button { height:42px; border:0; border-radius:10px; background:#111827; color:#fff; padding:0 16px; cursor:pointer; }
    .secondary-btn { background:#475569; }
    .upload-progress {
      margin-top: 16px;
      padding: 14px 16px;
      border: 1px solid #dbeafe;
      border-radius: 14px;
      background: linear-gradient(135deg, #f8fbff, #eef4ff);
    }
    .upload-progress-header {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      margin-bottom: 10px;
      color: #1e3a8a;
      font-weight: 600;
    }
    .upload-progress-track {
      height: 10px;
      border-radius: 999px;
      overflow: hidden;
      background: #dbeafe;
    }
    .upload-progress-fill {
      height: 100%;
      border-radius: inherit;
      background: linear-gradient(90deg, #2563eb, #38bdf8);
      transition: width 160ms ease;
    }
    .grid { display:grid; grid-template-columns:320px 1fr; gap:20px; }
    .left-panel, .right-panel { background:#fff; border:1px solid #e5e7eb; border-radius:16px; padding:16px; }
    .panel-title-row {
      display:flex;
      justify-content:space-between;
      align-items:flex-start;
      gap:16px;
      margin-bottom:16px;
      flex-wrap:wrap;
    }
    .panel-title { font-size:18px; font-weight:700; }
    .search-row {
      display:flex;
      gap:12px;
      align-items:center;
      flex-wrap:wrap;
    }
    .search-row input {
      min-width:320px;
      background:#f8fafc;
    }
    .search-summary {
      margin-bottom:12px;
      color:#475569;
      font-size:14px;
    }
    .danger { background: #b91c1c; }
    .preview-backdrop {
      position: fixed;
      inset: 0;
      background: rgba(15, 23, 42, 0.68);
      display: grid;
      place-items: center;
      padding: 24px;
      z-index: 1000;
    }
    .preview-modal {
      width: min(100%, 1100px);
      height: min(100%, 88vh);
      background: #fff;
      border-radius: 18px;
      box-shadow: 0 24px 80px rgba(15, 23, 42, 0.3);
      display: grid;
      grid-template-rows: auto 1fr;
      overflow: hidden;
    }
    .preview-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 16px;
      padding: 18px 22px;
      border-bottom: 1px solid #e5e7eb;
      background: linear-gradient(135deg, #f8fafc, #eef2ff);
    }
    .preview-header h3 { margin: 0; font-size: 1.05rem; }
    .preview-header p { margin: 4px 0 0; color: #64748b; font-size: 0.92rem; }
    .preview-close {
      height: auto;
      border-radius: 999px;
      padding: 10px 16px;
      background: #0f172a;
    }
    .preview-body {
      min-height: 0;
      background: #e2e8f0;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .preview-body iframe,
    .preview-body img {
      width: 100%;
      height: 100%;
      border: 0;
      object-fit: contain;
      background: #fff;
    }
  `]
})
export class ArchivePageComponent implements OnInit, OnDestroy {
  private readonly folderService = inject(FolderService);
  private readonly documentService = inject(DocumentService);
  private readonly notification = inject(NotificationService);
  private readonly tokenService = inject(TokenService);
  private readonly sanitizer = inject(DomSanitizer);

  readonly i18n = inject(I18nService);
  folders: FolderDto[] = [];
  documents: DocumentDto[] = [];
  selectedFolder: FolderDto | null = null;
  folderTree: FolderTreeNode[] = [];
  allFoldersFlat: FolderDto[] = [];
  newFolderName = '';
  uploadTitle = '';
  uploadDescription = '';
  selectedFile: File | null = null;
  isUploading = false;
  uploadProgress = 0;
  uploadFileName = '';
  searchQuery = '';
  activeSearchQuery = '';
  previewDocument: DocumentDto | null = null;
  previewUrl: string | null = null;
  previewResourceUrl: SafeResourceUrl | null = null;

  ngOnInit(): void {
    this.loadFolderTree();
    this.refreshDocumentList();
  }

  ngOnDestroy(): void {
    this.clearPreviewUrl();
  }

  get isSearchActive(): boolean {
    return this.activeSearchQuery.trim().length > 0;
  }

  get canDeleteDocuments(): boolean {
    return this.tokenService.hasPermission('documents.delete');
  }

  get documentPanelTitle(): string {
    return this.i18n.t('archive.documentsForPath', { path: this.selectedFolderDisplayPath });
  }

  get searchSummaryText(): string {
    const key = this.documents.length === 1 ? 'archive.searchSummary.one' : 'archive.searchSummary.other';
    return this.i18n.t(key, { count: this.documents.length, query: this.activeSearchQuery });
  }

  get selectedFolderDisplayPath(): string {
    if (!this.selectedFolder) return this.i18n.t('archive.root');

    const parts = this.selectedFolder.path
      .split('/')
      .filter(Boolean)
      .map(x => Number(x));

    const names: string[] = [this.i18n.t('archive.root')];

    for (const id of parts) {
      const match = this.allFoldersFlat.find(x => x.id === id);
      if (match) {
        names.push(match.name);
      }
    }

    names.push(this.selectedFolder.name);

    return names.join(' / ');
  }

  loadFolders(): void {
    this.loadChildren(null);
  }

  loadFolderTree(): void {
    this.folderService.getAll().subscribe({
      next: folders => {
        this.allFoldersFlat = folders;
        this.folderTree = this.buildFolderTree(folders);
      },
      error: () => this.notification.error(this.i18n.t('archive.failedLoadFolderTree'))
    });
  }

  loadDocuments(folderId: number | null): void {
    this.documentService.getByFolder(folderId).subscribe({
      next: documents => this.documents = documents,
      error: () => this.notification.error(this.i18n.t('archive.failedLoadDocuments'))
    });
  }

  applySearch(): void {
    const query = this.searchQuery.trim();
    if (!query) {
      this.clearSearch();
      return;
    }

    this.activeSearchQuery = query;
    this.documentService.search(query, this.selectedFolder?.id ?? null).subscribe({
      next: documents => this.documents = documents,
      error: () => this.notification.error(this.i18n.t('archive.failedSearchDocuments'))
    });
  }

  clearSearch(): void {
    this.searchQuery = '';
    this.activeSearchQuery = '';
    this.refreshDocumentList();
  }

  refreshDocumentList(): void {
    if (this.isSearchActive) {
      this.documentService.search(this.activeSearchQuery, this.selectedFolder?.id ?? null).subscribe({
        next: documents => this.documents = documents,
        error: () => this.notification.error(this.i18n.t('archive.failedSearchDocuments'))
      });
      return;
    }

    this.loadDocuments(this.selectedFolder?.id ?? null);
  }

  selectRoot(): void {
    this.closePreview();
    this.selectedFolder = null;
    this.loadChildren(null);
    this.refreshDocumentList();
  }

  buildFolderTree(folders: FolderDto[]): FolderTreeNode[] {
    const map = new Map<number, FolderTreeNode>();
    const roots: FolderTreeNode[] = [];

    for (const folder of folders) {
      map.set(folder.id, {
        ...folder,
        children: [],
        expanded: false
      });
    }

    for (const folder of folders) {
      const node = map.get(folder.id)!;

      if (folder.parentId == null) {
        roots.push(node);
      } else {
        const parent = map.get(folder.parentId);
        if (parent) {
          parent.children.push(node);
        } else {
          roots.push(node);
        }
      }
    }

    return roots;
  }

  onFolderSelected(folder: FolderDto): void {
    this.closePreview();
    this.selectedFolder = folder;
    this.expandParentChain(folder.id);
    this.refreshDocumentList();
  }

  expandParentChain(folderId: number): void {
    const folder = this.allFoldersFlat.find(x => x.id === folderId);
    if (!folder) return;

    const parentIds = folder.path
      .split('/')
      .filter(Boolean)
      .map(x => Number(x));

    const expandRecursive = (nodes: FolderTreeNode[]) => {
      for (const node of nodes) {
        if (parentIds.includes(node.id) || node.id === folderId) {
          node.expanded = true;
        }

        if (node.children.length > 0) {
          expandRecursive(node.children);
        }
      }
    };

    expandRecursive(this.folderTree);
  }

  createFolder(): void {
    const parentId = this.selectedFolder?.id ?? null;

    this.folderService.create({
      parentId,
      name: this.newFolderName.trim()
    }).subscribe({
      next: folder => {
        this.newFolderName = '';
        this.notification.success(this.i18n.t('archive.folderCreated', { name: folder.name }));

        if (parentId === null) {
          this.loadFolders();
        } else {
          this.loadChildren(parentId);
        }
      },
      error: () => this.notification.error(this.i18n.t('archive.failedLoadFolders'))
    });
  }

  loadChildren(parentId: number | null): void {
    this.folderService.getChildren(parentId).subscribe({
      next: folders => this.folders = folders,
      error: () => this.notification.error(this.i18n.t('archive.failedLoadFolders'))
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.selectedFile = input.files?.[0] ?? null;
  }

  canUpload(): boolean {
    return !!this.selectedFolder && !!this.uploadTitle.trim() && !!this.selectedFile;
  }

  uploadDocument(): void {
    if (!this.selectedFolder || !this.selectedFile || this.isUploading) return;

    this.isUploading = true;
    this.uploadProgress = 0;
    this.uploadFileName = this.selectedFile.name;

    this.documentService.upload({
      folderId: this.selectedFolder.id,
      title: this.uploadTitle.trim(),
      description: this.uploadDescription.trim(),
      file: this.selectedFile
    }).subscribe({
      next: event => {
        if (event.type === HttpEventType.UploadProgress) {
          const total = event.total ?? this.selectedFile?.size ?? 0;
          this.uploadProgress = total > 0
            ? Math.round((event.loaded / total) * 100)
            : this.uploadProgress;
          return;
        }

        if (event.type === HttpEventType.Response) {
          this.uploadProgress = 100;
          this.uploadTitle = '';
          this.uploadDescription = '';
          this.selectedFile = null;
          this.notification.success(this.i18n.t('archive.documentUploaded'));
          this.refreshDocumentList();
          setTimeout(() => this.resetUploadState(), 500);
        }
      },
      error: err => {
        this.resetUploadState();
        void this.showDocumentActionError('upload', err);
      }
    });
  }

  openDocument(document: DocumentDto): void {
    this.documentService.download(document.id).subscribe({
      next: response => {
        const blob = response.body;
        if (!blob) {
          this.notification.error(this.i18n.t('archive.emptyDownload'));
          return;
        }

        const fileName = getFileNameFromContentDisposition(response.headers.get('content-disposition'));
        const mimeType = blob.type || document.mimeType;

        if (!canPreviewMimeType(mimeType)) {
          downloadBlob(blob, fileName);
          return;
        }

        this.clearPreviewUrl();
        this.previewDocument = { ...document, mimeType };
        this.previewUrl = createObjectUrl(blob);
        this.previewResourceUrl = mimeType === 'application/pdf'
          ? this.sanitizer.bypassSecurityTrustResourceUrl(this.previewUrl)
          : null;
      },
      error: err => void this.showDocumentActionError('download', err)
    });
  }

  closePreview(): void {
    this.previewDocument = null;
    this.clearPreviewUrl();
  }

  deleteSelectedFolder(): void {
    if (!this.selectedFolder) return;

    const confirmed = confirm(this.i18n.t('archive.deleteFolderConfirm', { name: this.selectedFolder.name }));
    if (!confirmed) return;

    this.folderService.delete(this.selectedFolder.id).subscribe({
      next: () => {
        this.notification.success(this.i18n.t('archive.folderDeleted'));
        this.selectedFolder = null;
        this.loadFolderTree();
        this.refreshDocumentList();
      },
      error: (err) => {
        const message = err?.error?.message || this.i18n.t('archive.failedDeleteFolder');
        this.notification.error(message);
      }
    });
  }

  deleteDocument(document: DocumentDto): void {
    if (!this.canDeleteDocuments) {
      this.notification.error(this.i18n.t('archive.noDeletePermission'));
      return;
    }

    const confirmed = confirm(this.i18n.t('archive.deleteDocumentConfirm', { name: document.title }));
    if (!confirmed) return;

    this.documentService.delete(document.id).subscribe({
      next: () => {
        this.notification.success(this.i18n.t('archive.documentDeleted'));
        this.refreshDocumentList();
      },
      error: err => void this.showDocumentActionError('delete', err)
    });
  }

  private resetUploadState(): void {
    this.isUploading = false;
    this.uploadProgress = 0;
    this.uploadFileName = '';
  }

  private clearPreviewUrl(): void {
    revokeObjectUrl(this.previewUrl);
    this.previewUrl = null;
    this.previewResourceUrl = null;
  }

  private async showDocumentActionError(
    action: 'upload' | 'download' | 'delete',
    error: unknown
  ): Promise<void> {
    this.notification.error(await getDocumentActionErrorMessage(action, error));
  }
}
